import { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import { api, API_BASE } from '../../lib/api';
import { ssoLoginPath } from '../../lib/ssoLogin';
import { useAuthStore } from '../../stores';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

type Mode = 'login' | 'register';

interface SsoProvider {
  key: string;
  label: string;
  enabled: boolean;
  type: string;
}

/* ── Eurion globe logo: the same file as the sidebar and eurion.se (public/logo-mark.svg) ── */
function EurionLogo({ className = 'h-12 w-12' }: { className?: string }) {
  return <img src="/logo-mark.svg" alt="" aria-hidden="true" className={className} />;
}

/* ── Microsoft four-square icon ── */
function MicrosoftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function SamlIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );
}

/* BankID official blue + white fingerprint-style icon */
function BankIdIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="8" fill="#235971"/>
      <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
        fontSize="10" fontWeight="700" fill="white" fontFamily="sans-serif">
        BID
      </text>
    </svg>
  );
}

function FrejaIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="8" fill="#00274C"/>
      <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
        fontSize="9" fontWeight="700" fill="#F7B731" fontFamily="sans-serif">
        FREJA
      </text>
    </svg>
  );
}

/* ── BankID hint code → human-readable text in the app language ── */
function bankidHintText(t: TFunction, hint: string): string {
  const map: Record<string, string> = {
    outstandingTransaction: t('authUi.bankid.hint.startApp', 'Start your BankID app'),
    noClient:               t('authUi.bankid.hint.startApp', 'Start your BankID app'),
    started:                t('authUi.bankid.hint.opening', 'Opening BankID…'),
    userSign:               t('authUi.bankid.hint.userSign', 'Confirm your identity in the BankID app'),
    expiredTransaction:     t('authUi.bankid.hint.expired', 'The session expired — please try again'),
    certificateErr:         t('authUi.bankid.hint.certificateErr', 'Invalid BankID certificate'),
    userCancel:             t('authUi.bankid.hint.userCancel', 'You cancelled the authentication'),
    cancelled:              t('authUi.bankid.hint.cancelled', 'Cancelled'),
    startFailed:            t('authUi.bankid.hint.startFailed', 'BankID could not start — try a QR code scan instead'),
  };
  return map[hint] ?? hint;
}

interface SsoError { reason: string; ref: string | null }

/** #sso_error=<reason>&ref=<id> (or the query) that a failed SSO callback sends back. */
function readSsoError(loc: { hash: string; search: string }): SsoError | null {
  for (const params of [new URLSearchParams(loc.hash.slice(1)), new URLSearchParams(loc.search)]) {
    const reason = params.get('sso_error');
    if (reason) return { reason, ref: params.get('ref') };
  }
  return null;
}

// Read when the app loads and kept in sessionStorage until the user tries to sign in again:
// routing (lib/route.ts) rewrites the URL, and on a first visit the service worker taking
// control reloads the page (main.tsx), which dropped the message before anyone saw it.
const SSO_ERROR_KEY = 'eurion_sso_error';
function storeSsoError(e: SsoError | null): void {
  try {
    if (e) sessionStorage.setItem(SSO_ERROR_KEY, JSON.stringify(e));
    else sessionStorage.removeItem(SSO_ERROR_KEY);
  } catch { /* storage blocked: the message just won't survive a reload */ }
}
let startupSsoError: SsoError | null = typeof window === 'undefined' ? null : readSsoError(window.location);
if (startupSsoError) storeSsoError(startupSsoError);
function takeStartupSsoError(): SsoError | null {
  if (startupSsoError) return startupSsoError;
  try {
    const saved = sessionStorage.getItem(SSO_ERROR_KEY);
    return saved ? (JSON.parse(saved) as SsoError) : null;
  } catch {
    return null;
  }
}
function clearStartupSsoError(): void {
  startupSsoError = null;
  storeSsoError(null);
}

export function LoginPage() {
  const { t } = useTranslation();
  const { setUser } = useAuthStore();
  const [mode, setMode] = useState<Mode>('login');

  // Login fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Register fields
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regName, setRegName] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);

  // BankID modal state
  const [bankidModal, setBankidModal] = useState(false);
  const [bankidOrderRef, setBankidOrderRef] = useState('');
  const [bankidAutoStartUrl, setBankidAutoStartUrl] = useState('');
  const [bankidQrStartToken, setBankidQrStartToken] = useState('');
  // qrStartSecret is stored server-side; client uses the /qr/:ref endpoint to get pre-computed strings
  const [bankidStatus, setBankidStatus] = useState<'starting' | 'pending' | 'complete' | 'failed'>('starting');
  const [bankidHint, setBankidHint] = useState('');
  const bankidCanvasRef = useRef<HTMLCanvasElement>(null);
  const bankidPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bankidQrRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bankidElapsedRef = useRef(0);

  // Freja eID modal state
  const [frejaModal, setFrejaModal] = useState(false);
  const [frejaAuthRef, setFrejaAuthRef] = useState('');
  const [frejaStatus, setFrejaStatus] = useState<'starting' | 'pending' | 'complete' | 'failed'>('starting');
  const [frejaStatusText, setFrejaStatusText] = useState('');
  const [frejaAppLink, setFrejaAppLink] = useState('');
  const frejaPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Shared: finish login with tokens from any provider ──────
  const finishLogin = useCallback(async (
    accessToken: string,
    refreshToken: string,
  ) => {
    api.setToken(accessToken);
    api.setRefreshToken(refreshToken);
    const res = await api.get<{
      user: { id: string; email: string; displayName: string; orgId: string; roles: string[] };
    }>('/v1/auth/me');
    if (res.success && res.data) {
      setUser({
        id: res.data.user.id,
        email: res.data.user.email,
        displayName: res.data.user.displayName,
        orgId: res.data.user.orgId,
        roles: res.data.user.roles,
      });
    }
  }, [setUser]);

  // ── Cleanup helpers ──────────────────────────────────────────
  const stopBankId = useCallback(() => {
    if (bankidPollRef.current) clearInterval(bankidPollRef.current);
    if (bankidQrRef.current)   clearInterval(bankidQrRef.current);
    bankidPollRef.current = null;
    bankidQrRef.current   = null;
    bankidElapsedRef.current = 0;
  }, []);

  const closeBankIdModal = useCallback(() => {
    stopBankId();
    setBankidModal(false);
    setBankidOrderRef('');
    setBankidStatus('starting');
    setBankidHint('');
  }, [stopBankId]);

  const stopFreja = useCallback(() => {
    if (frejaPollRef.current) clearInterval(frejaPollRef.current);
    frejaPollRef.current = null;
  }, []);

  const closeFrejaModal = useCallback(() => {
    stopFreja();
    setFrejaModal(false);
    setFrejaAuthRef('');
    setFrejaStatus('starting');
    setFrejaStatusText('');
  }, [stopFreja]);

  // ── BankID QR animation ───────────────────────────────────────
  useEffect(() => {
    if (!bankidModal || bankidStatus !== 'pending' || !bankidQrStartToken) return;

    const renderQr = async (elapsed: number) => {
      if (!bankidCanvasRef.current) return;
      try {
        const res = await api.get<{ qrString: string }>(
          `/v1/auth/bankid/qr/${bankidOrderRef}?t=${elapsed}`,
        );
        if (res.success && res.data?.qrString) {
          await QRCode.toCanvas(bankidCanvasRef.current, res.data.qrString, {
            width: 200,
            margin: 2,
            color: { dark: '#2E3440', light: '#ECEFF4' },
          });
        }
      } catch { /* ignore render errors */ }
    };

    void renderQr(0);
    bankidQrRef.current = setInterval(() => {
      bankidElapsedRef.current += 1;
      void renderQr(bankidElapsedRef.current);
    }, 1000);

    return () => {
      if (bankidQrRef.current) clearInterval(bankidQrRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankidModal, bankidStatus, bankidOrderRef, bankidQrStartToken]);

  // ── BankID handler ───────────────────────────────────────────
  const handleBankIdLogin = useCallback(async () => {
    setBankidStatus('starting');
    setBankidModal(true);
    setBankidHint('');

    try {
      const startRes = await api.post<{
        orderRef: string;
        autoStartToken: string;
        qrStartToken: string;
        qrStartSecret: string;
        autoStartUrl: string;
      }>('/v1/auth/bankid/start', {});

      if (!startRes.success || !startRes.data) {
        setBankidStatus('failed');
        setBankidHint(startRes.error?.message ?? t('authUi.bankid.couldNotStart', 'Could not start BankID'));
        return;
      }

      const { orderRef, autoStartUrl, qrStartToken } = startRes.data;
      setBankidOrderRef(orderRef);
      setBankidAutoStartUrl(autoStartUrl);
      setBankidQrStartToken(qrStartToken);
      setBankidStatus('pending');

      // Poll collect every 2 seconds
      bankidPollRef.current = setInterval(async () => {
        try {
          const res = await api.get<{
            status: string;
            hintCode?: string;
            accessToken?: string;
            refreshToken?: string;
          }>(`/v1/auth/bankid/collect/${orderRef}`);

          if (!res.success || !res.data) {
            // Refused (identity not linked to an account), already used or expired: stop polling
            if (res.error && /^BANKID_(LOGIN_FAILED|ORDER_USED|NOT_FOUND|LINK_FAILED)$/.test(res.error.code)) {
              stopBankId();
              setBankidStatus('failed');
              setBankidHint(res.error.message);
            }
            return;
          }
          const { status, hintCode, accessToken, refreshToken } = res.data;

          if (hintCode) setBankidHint(hintCode);

          if (status === 'complete' && accessToken && refreshToken) {
            stopBankId();
            setBankidStatus('complete');
            await finishLogin(accessToken, refreshToken);
          } else if (status === 'failed' || status === 'cancelled') {
            stopBankId();
            setBankidStatus('failed');
          }
        } catch { /* network blip — continue polling */ }
      }, 2000);
    } catch {
      setBankidStatus('failed');
      setBankidHint(t('authUi.eid.networkError', 'Network error — please try again'));
    }
  }, [finishLogin, stopBankId, t]);

  // ── Freja eID+ handler ───────────────────────────────────────
  const handleFrejaLogin = useCallback(async () => {
    setFrejaStatus('starting');
    setFrejaModal(true);
    setFrejaStatusText('');

    try {
      const startRes = await api.post<{ authRef: string; appLink: string }>(
        '/v1/auth/freja/start',
        { level: 'PLUS' },
      );

      if (!startRes.success || !startRes.data) {
        setFrejaStatus('failed');
        setFrejaStatusText(startRes.error?.message ?? t('authUi.freja.couldNotStart', 'Could not start Freja eID+'));
        return;
      }

      const { authRef, appLink } = startRes.data;
      setFrejaAuthRef(authRef);
      setFrejaAppLink(appLink);
      setFrejaStatus('pending');

      const FREJA_HINT: Record<string, string> = {
        STARTED:            t('authUi.freja.hint.started', 'Waiting for Freja eID+ app…'),
        DELIVERED_TO_MOBILE:t('authUi.freja.hint.delivered', 'Request delivered — open the Freja eID+ app to approve'),
        CANCELLED:          t('authUi.freja.hint.cancelled', 'Authentication was cancelled'),
        REJECTED:           t('authUi.freja.hint.rejected', 'Authentication was rejected'),
        EXPIRED:            t('authUi.freja.hint.expired', 'Request expired — please try again'),
        RP_CANCELLED:       t('authUi.freja.hint.rpCancelled', 'Authentication cancelled'),
      };

      // Poll collect every 2 seconds
      frejaPollRef.current = setInterval(async () => {
        try {
          const res = await api.get<{
            status: string;
            terminal?: boolean;
            accessToken?: string;
            refreshToken?: string;
          }>(`/v1/auth/freja/collect/${authRef}`);

          if (!res.success || !res.data) {
            // Refused (identity not linked to an account), already used or expired: stop polling
            if (res.error && /^FREJA_(LOGIN_FAILED|ORDER_USED|NOT_FOUND|LINK_FAILED)$/.test(res.error.code)) {
              stopFreja();
              setFrejaStatus('failed');
              setFrejaStatusText(res.error.message);
            }
            return;
          }
          const { status, accessToken, refreshToken } = res.data;

          setFrejaStatusText(FREJA_HINT[status] ?? status);

          if (status === 'APPROVED' && accessToken && refreshToken) {
            stopFreja();
            setFrejaStatus('complete');
            await finishLogin(accessToken, refreshToken);
          } else if (['CANCELLED', 'REJECTED', 'EXPIRED', 'RP_CANCELLED'].includes(status)) {
            stopFreja();
            setFrejaStatus('failed');
          }
        } catch { /* network blip — continue polling */ }
      }, 2000);
    } catch {
      setFrejaStatus('failed');
      setFrejaStatusText(t('authUi.eid.networkError', 'Network error — please try again'));
    }
  }, [finishLogin, stopFreja, t]);


  useEffect(() => {
    // Tokens come back in the fragment (identity sso-return.ts); older servers used the query
    const fromHash = new URLSearchParams(window.location.hash.slice(1));
    const params = fromHash.has('access_token') ? fromHash : new URLSearchParams(window.location.search);
    const accessToken  = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    // A failed SSO sign-in comes back as #sso_error=<reason>&ref=<request id> (identity sso-return.ts)
    const fromUrl = readSsoError(window.location);
    if (fromUrl) storeSsoError(fromUrl);
    const sso = fromUrl ?? takeStartupSsoError();
    if (sso) {
      const { reason: ssoError, ref } = sso;
      if (window.location.hash || window.location.search) window.history.replaceState({}, '', window.location.pathname);
      setError(ssoError === 'account_disabled'
        ? t('authUi.ssoDisabled', 'Your account is deactivated. Contact your administrator.')
        : ref
          ? t('authUi.ssoFailedRef', 'SSO login failed. Please try again. If it keeps failing, give your administrator this reference: {{ref}}', { ref })
          : t('authUi.ssoFailed', 'SSO login failed. Please try again.'));
      return;
    }

    if (accessToken && refreshToken) {
      window.history.replaceState({}, '', window.location.pathname);
      api.setToken(accessToken);
      api.setRefreshToken(refreshToken);

      (async () => {
        try {
          const res = await api.get<{
            user: { id: string; email: string; displayName: string; orgId: string; roles: string[] };
          }>('/v1/auth/me');
          if (res.success && res.data) {
            setUser({
              id: res.data.user.id,
              email: res.data.user.email,
              displayName: res.data.user.displayName,
              orgId: res.data.user.orgId,
              roles: res.data.user.roles,
            });
          }
        } catch {
          setError(t('authUi.ssoFailed', 'SSO login failed. Please try again.'));
        }
      })();
    }
  }, [setUser, t]);

  // ── Fetch available SSO providers ──
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ providers: SsoProvider[] }>('/v1/auth/providers');
        if (res.success && res.data) {
          const filtered = res.data.providers.filter(p => p.key !== 'local' && p.enabled);
          setSsoProviders(filtered);
        }
      } catch {
        // SSO providers unavailable — fall back to local auth only
      }
    })();
  }, []);

  const handleSsoLogin = (providerKey: string) => {
    clearStartupSsoError();
    if (providerKey === 'bankid') { void handleBankIdLogin(); return; }
    if (providerKey === 'freja')  { void handleFrejaLogin();  return; }
    // The desktop app loads the server itself, so its origin is the real one; only a
    // bundled copy (custom tauri:// origin) needs an http(s) origin to return to.
    const servedOverHttp = /^https?:$/.test(window.location.protocol);
    const origin = servedOverHttp ? window.location.origin : 'https://app.eurion.se';
    const url = ssoLoginPath(providerKey, origin, API_BASE);
    if (url) window.location.href = url;
  };

  const getSsoIcon = (key: string) => {
    switch (key) {
      case 'azure':   return <MicrosoftIcon />;
      case 'google':  return <GoogleIcon />;
      case 'saml':    return <SamlIcon />;
      case 'bankid':  return <BankIdIcon />;
      case 'freja':   return <FrejaIcon />;
      default: return null;
    }
  };

  const getSsoLabel = (key: string, fallback: string) => {
    switch (key) {
      case 'azure':  return 'Microsoft Entra ID';
      case 'google': return 'Google Workspace';
      case 'saml':   return t('authUi.sso.enterprise', 'Enterprise SSO');
      case 'bankid': return 'BankID';
      case 'freja':  return 'Freja eID+';
      default: return fallback;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearStartupSsoError();
    setError('');
    setLoading(true);
    try {
      const res = await api.post<{
        tokens: { accessToken: string; refreshToken: string };
        user: { id: string; email: string; displayName: string; orgId: string; roles: string[] };
      }>('/v1/auth/login', { email, password });
      if (res.success && res.data) {
        api.setToken(res.data.tokens.accessToken);
        api.setRefreshToken(res.data.tokens.refreshToken);
        setUser({
          id: res.data.user.id,
          email: res.data.user.email,
          displayName: res.data.user.displayName,
          orgId: res.data.user.orgId,
          roles: res.data.user.roles,
        });
      } else {
        setError(res.error?.message ?? t('authUi.invalidCredentials', 'Invalid credentials'));
      }
    } catch {
      setError(t('authUi.connectionError', 'Connection error. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (regPassword !== regConfirm) {
      setError(t('authUi.register.mismatch', 'Passwords do not match'));
      return;
    }
    if (regPassword.length < 8) {
      setError(t('authUi.register.tooShort', 'Password must be at least 8 characters'));
      return;
    }

    setLoading(true);
    try {
      const res = await api.post<{
        status?: string;
        tokens?: { accessToken: string; refreshToken: string };
        user?: { id: string; email: string; displayName: string; orgId: string; roles: string[] };
      }>(
        '/v1/auth/register',
        { email: regEmail, password: regPassword, displayName: regName },
      );
      if (res.success && res.data?.status === 'CONFIRMATION_SENT') {
        // The account is created from the link in the e-mail (identity ID-F15)
        setSuccess(t('authUi.register.checkEmail', 'Check your e-mail: we sent you a link to finish creating your account.'));
        setRegPassword('');
        setRegConfirm('');
      } else if (res.success && res.data?.tokens && res.data.user) {
        api.setToken(res.data.tokens.accessToken);
        api.setRefreshToken(res.data.tokens.refreshToken);
        setUser({
          id: res.data.user.id,
          email: res.data.user.email,
          displayName: res.data.user.displayName,
          orgId: res.data.user.orgId,
          roles: res.data.user.roles,
        });
      } else {
        setError(res.error?.message ?? t('authUi.register.failed', 'Registration failed'));
      }
    } catch {
      setError(t('authUi.connectionError', 'Connection error. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  /* ── shared input classes ── */
  const inputCls =
    'w-full rounded-lg border border-nord-3/60 bg-nord-0 px-4 py-2.5 text-sm text-nord-6 placeholder-nord-muted focus:border-nord-8 focus:outline-none focus:ring-2 focus:ring-nord-8/20 transition-all';

  return (
    <div className="flex min-h-screen items-center justify-center bg-nord-0 px-4">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-nord-10/5 via-transparent to-nord-15/5 pointer-events-none" />

      <div className="relative w-full max-w-[400px] rounded-2xl border border-nord-3/40 bg-nord-1 p-8 shadow-lifted animate-modal-in">

        {/* ── Logo + heading ── */}
        <div className="mb-8 text-center">
          <div className="mb-4 flex items-center justify-center gap-3">
            <EurionLogo className="h-10 w-10" />
            <span className="text-2xl font-bold tracking-tight text-nord-6">Eurion</span>
          </div>
          <h1 className="text-lg font-semibold text-nord-6">{t('authUi.tagline', 'Secure EU Collaboration')}</h1>
          <p className="mt-2 text-sm leading-relaxed text-nord-muted">
            {t('authUi.subtitle', 'Private, EU-hosted communication for organizations that need security compliance')}
          </p>
        </div>

        {/* ── Login form ── */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-nord-4" htmlFor="email">
                {t('authUi.email', 'Email')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@organisation.eu"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-nord-4" htmlFor="password">
                {t('auth.password', 'Password')}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                aria-required="true"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p role="alert" aria-live="assertive" className="rounded-lg bg-nord-11/10 border border-nord-11/20 px-3 py-2 text-sm text-nord-11 animate-slide-up">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-nord-10 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-nord-9 hover:shadow-glow-sm active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? t('authUi.signingIn', 'Signing in…') : t('authUi.signInButton', 'Sign In')}
            </button>

            {/* ── SSO Providers ── */}
            {ssoProviders.length > 0 && (
              <div className="space-y-3 pt-1">
                <div className="divider-label">
                  <span>{t('authUi.orSignInWith', 'or sign in with')}</span>
                </div>
                {ssoProviders.map(p => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => handleSsoLogin(p.key)}
                    className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-nord-3/50 bg-nord-2/50 py-2.5 text-sm font-medium text-nord-6 transition-all hover:border-nord-3 hover:bg-nord-2 active:scale-[0.98]"
                  >
                    {getSsoIcon(p.key)}
                    {getSsoLabel(p.key, p.label)}
                  </button>
                ))}
              </div>
            )}

            {/* Link to register */}
            <p className="pt-1 text-center text-xs text-nord-muted">
              {t('authUi.noAccount', "Don't have an account?")}{' '}
              <button
                type="button"
                onClick={() => { setMode('register'); setError(''); }}
                className="text-nord-8 hover:text-nord-7 hover:underline transition-colors"
              >
                {t('authUi.createOne', 'Create one')}
              </button>
            </p>
          </form>
        )}

        {/* ── Register form ── */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-nord-4" htmlFor="reg-name">
                {t('authUi.register.fullName', 'Full name')}
              </label>
              <input
                id="reg-name"
                type="text"
                autoComplete="name"
                required
                aria-required="true"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                className={inputCls}
                placeholder="Anna Lindström"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-nord-4" htmlFor="reg-email">
                {t('authUi.email', 'Email')}
              </label>
              <input
                id="reg-email"
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                className={inputCls}
                placeholder="you@organisation.eu"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-nord-4" htmlFor="reg-password">
                {t('auth.password', 'Password')}
              </label>
              <input
                id="reg-password"
                type="password"
                autoComplete="new-password"
                required
                aria-required="true"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                className={inputCls}
                placeholder={t('authUi.register.minChars', 'Min. 8 characters')}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-nord-4" htmlFor="reg-confirm">
                {t('authUi.register.confirm', 'Confirm password')}
              </label>
              <input
                id="reg-confirm"
                type="password"
                autoComplete="new-password"
                required
                aria-required="true"
                value={regConfirm}
                onChange={(e) => setRegConfirm(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p role="alert" aria-live="assertive" className="rounded-lg bg-nord-11/10 border border-nord-11/20 px-3 py-2 text-sm text-nord-11 animate-slide-up">{error}</p>
            )}
            {success && (
              <p role="status" aria-live="polite" className="rounded-lg bg-aurora-3/10 border border-aurora-3/20 px-3 py-2 text-sm text-aurora-3 animate-slide-up">{success}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-nord-10 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-nord-9 hover:shadow-glow-sm active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? t('authUi.register.creating', 'Creating account…') : t('auth.register', 'Create account')}
            </button>

            <p className="pt-1 text-center text-xs text-nord-muted">
              {t('authUi.haveAccount', 'Already have an account?')}{' '}
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="text-nord-8 hover:text-nord-7 hover:underline transition-colors"
              >
                {t('auth.signIn', 'Sign in')}
              </button>
            </p>
          </form>
        )}

        {/* ── Compliance footer ── */}
        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-nord-muted">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-aurora-3/60" />
            {t('authUi.footer.residency', 'EU data residency')}
          </span>
          <span className="text-nord-muted/40">·</span>
          <span>{t('authUi.footer.gdpr', 'GDPR compliant')}</span>
          <span className="text-nord-muted/40">·</span>
          <span>{t('trust.summary', 'Encrypted in transit')}</span>
        </div>
      </div>

      {/* ── BankID Modal ──────────────────────────────────────────── */}
      {bankidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-modal-in">
          <div className="w-full max-w-sm rounded-2xl border border-nord-3/40 bg-nord-1 p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BankIdIcon />
                <span className="font-semibold text-nord-6">BankID</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (bankidOrderRef) {
                    void api.post(`/v1/auth/bankid/cancel/${bankidOrderRef}`, {});
                  }
                  closeBankIdModal();
                }}
                className="rounded p-1 text-nord-muted hover:text-nord-6 transition-colors"
                aria-label={t('common.close', 'Close')}
              >✕</button>
            </div>

            {bankidStatus === 'starting' && (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-nord-3 border-t-nord-8" />
                <p className="text-sm text-nord-muted">{t('authUi.bankid.starting', 'Starting BankID…')}</p>
              </div>
            )}

            {bankidStatus === 'pending' && (
              <div className="flex flex-col items-center gap-4">
                <p className="text-center text-sm text-nord-4">
                  {t('authUi.bankid.scan', 'Open BankID on your phone and scan the QR code')}
                </p>
                <canvas
                  ref={bankidCanvasRef}
                  width={200}
                  height={200}
                  className="rounded-xl border border-nord-3/30"
                  aria-label={t('authUi.bankid.qrLabel', 'BankID QR code')}
                />
                <div className="h-px w-full bg-nord-3/30" />
                <p className="text-xs text-nord-muted">{t('authUi.bankid.orThisDevice', 'or open BankID on this device')}</p>
                <a
                  href={bankidAutoStartUrl}
                  className="w-full rounded-lg border border-nord-8/40 bg-nord-8/10 py-2 text-center text-sm font-medium text-nord-8 hover:bg-nord-8/20 transition-colors"
                >
                  {t('authUi.bankid.openApp', 'Open BankID app')}
                </a>
                {bankidHint && (
                  <p className="text-center text-xs text-nord-muted">
                    {bankidHintText(t, bankidHint)}
                  </p>
                )}
              </div>
            )}

            {bankidStatus === 'complete' && (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-aurora-3/20">
                  <span className="text-2xl">✓</span>
                </div>
                <p className="text-sm text-nord-4">{t('authUi.eid.success', 'Authentication successful — signing you in…')}</p>
              </div>
            )}

            {bankidStatus === 'failed' && (
              <div className="flex flex-col items-center gap-3 py-4">
                <p className="text-sm text-nord-11">{bankidHint ? bankidHintText(t, bankidHint) : t('authUi.eid.failed', 'Authentication failed')}</p>
                <button
                  type="button"
                  onClick={() => { closeBankIdModal(); void handleBankIdLogin(); }}
                  className="rounded-lg bg-nord-10 px-4 py-2 text-sm font-medium text-white hover:bg-nord-9 transition-colors"
                >
                  {t('authUi.eid.tryAgain', 'Try again')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Freja eID+ Modal ──────────────────────────────────────── */}
      {frejaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-modal-in">
          <div className="w-full max-w-sm rounded-2xl border border-nord-3/40 bg-nord-1 p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FrejaIcon />
                <span className="font-semibold text-nord-6">Freja eID+</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (frejaAuthRef) {
                    void api.post(`/v1/auth/freja/cancel/${frejaAuthRef}`, {});
                  }
                  closeFrejaModal();
                }}
                className="rounded p-1 text-nord-muted hover:text-nord-6 transition-colors"
                aria-label={t('common.close', 'Close')}
              >✕</button>
            </div>

            {frejaStatus === 'starting' && (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-nord-3 border-t-nord-8" />
                <p className="text-sm text-nord-muted">{t('authUi.freja.starting', 'Starting Freja eID+…')}</p>
              </div>
            )}

            {frejaStatus === 'pending' && (
              <div className="flex flex-col items-center gap-4">
                <p className="text-center text-sm text-nord-4">
                  {t('authUi.freja.approve', 'Open the Freja eID+ app on your phone to approve')}
                </p>
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-nord-3 bg-nord-2">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-nord-3 border-t-nord-8" />
                </div>
                {frejaAppLink && (
                  <a
                    href={frejaAppLink}
                    className="w-full rounded-lg border border-nord-8/40 bg-nord-8/10 py-2 text-center text-sm font-medium text-nord-8 hover:bg-nord-8/20 transition-colors"
                  >
                    {t('authUi.freja.openApp', 'Open Freja eID+ app')}
                  </a>
                )}
                {frejaStatusText && (
                  <p className="text-center text-xs text-nord-muted">{frejaStatusText}</p>
                )}
              </div>
            )}

            {frejaStatus === 'complete' && (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-aurora-3/20">
                  <span className="text-2xl">✓</span>
                </div>
                <p className="text-sm text-nord-4">{t('authUi.eid.success', 'Authentication successful — signing you in…')}</p>
              </div>
            )}

            {frejaStatus === 'failed' && (
              <div className="flex flex-col items-center gap-3 py-4">
                <p className="text-sm text-nord-11">{frejaStatusText || t('authUi.eid.failed', 'Authentication failed')}</p>
                <button
                  type="button"
                  onClick={() => { closeFrejaModal(); void handleFrejaLogin(); }}
                  className="rounded-lg bg-nord-10 px-4 py-2 text-sm font-medium text-white hover:bg-nord-9 transition-colors"
                >
                  {t('authUi.eid.tryAgain', 'Try again')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
